/**
 * JSON/CSV Converter utility functions
 * Complete implementation with papaparse for bidirectional conversion
 */

import Papa from 'papaparse'

export interface ConversionOptions {
  selectedFields?: string[]
  customFieldNames?: Record<string, string>
  flattenNested?: boolean
  inferTypes?: boolean
  delimiter?: string
}

export interface ConversionResult {
  data: string | object[]
  preview: string[][]
  fields: string[]
  rowCount: number
}

/**
 * Flatten nested JSON object
 */
function flattenObject(obj: any, prefix = ''): Record<string, any> {
  const flattened: Record<string, any> = {}

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key

      if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        Object.assign(flattened, flattenObject(obj[key], newKey))
      } else if (Array.isArray(obj[key])) {
        flattened[newKey] = JSON.stringify(obj[key])
      } else {
        flattened[newKey] = obj[key]
      }
    }
  }

  return flattened
}

/**
 * Unflatten object back to nested structure
 */
function unflattenObject(obj: Record<string, any>): any {
  const result: any = {}

  for (const key in obj) {
    const keys = key.split('.')
    let current = result

    for (let i = 0; i < keys.length - 1; i++) {
      if (!(keys[i] in current)) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }

    // Try to parse JSON arrays
    const value = obj[key]
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        current[keys[keys.length - 1]] = JSON.parse(value)
      } catch {
        current[keys[keys.length - 1]] = value
      }
    } else {
      current[keys[keys.length - 1]] = value
    }
  }

  return result
}

/**
 * Infer type from string value
 */
function inferType(value: unknown): unknown {
  // Papa may already have converted numbers, booleans, dates or empty cells.
  if (typeof value !== 'string') return value
  if (value === '') return null
  if (value === 'null') return null
  if (value === 'true') return true
  if (value === 'false') return false

  // Try number
  const num = Number(value)
  if (!isNaN(num) && value.trim() !== '') {
    return num
  }

  // Try date
  const date = new Date(value)
  if (!isNaN(date.getTime()) && value.match(/\d{4}-\d{2}-\d{2}/)) {
    return value // Keep as string for CSV compatibility
  }

  return value
}

/**
 * Convert JSON to CSV
 */
export function jsonToCsv(
  jsonData: any[],
  options: ConversionOptions = {}
): ConversionResult {
  const { selectedFields, customFieldNames, flattenNested, delimiter = ',' } = options

  if (!Array.isArray(jsonData) || jsonData.length === 0) {
    throw new Error('Invalid JSON data: expected non-empty array')
  }

  // Flatten if requested
  let processedData = flattenNested
    ? jsonData.map(item => flattenObject(item))
    : jsonData

  // Get all fields from the data
  const allFields = new Set<string>()
  processedData.forEach(row => {
    Object.keys(row).forEach(key => allFields.add(key))
  })

  // Filter fields if specified
  const fields = selectedFields && selectedFields.length > 0
    ? selectedFields.filter(f => allFields.has(f))
    : Array.from(allFields)

  // Apply custom field names
  const fieldMapping = customFieldNames || {}
  const headers = fields.map(f => fieldMapping[f] || f)

  // Convert to CSV
  const csvData = Papa.unparse({
    fields: headers,
    data: processedData.map(row =>
      fields.map(f => {
        const value = row[f]
        return value !== undefined && value !== null ? String(value) : ''
      })
    )
  }, { delimiter })

  // Generate preview (first 10 rows)
  const preview: string[][] = [headers]
  const previewRows = processedData.slice(0, 10).map(row =>
    fields.map(f => String(row[f] !== undefined ? row[f] : ''))
  )
  preview.push(...previewRows)

  return {
    data: csvData,
    preview,
    fields,
    rowCount: processedData.length
  }
}

/**
 * Convert CSV to JSON
 */
export function csvToJson(
  csvData: string,
  options: ConversionOptions = {}
): Promise<ConversionResult> {
  const { inferTypes, flattenNested, delimiter = ',' } = options

  return new Promise<ConversionResult>((resolve, reject) => {
    Papa.parse(csvData, {
      header: true,
      delimiter,
      skipEmptyLines: true,
      dynamicTyping: inferTypes || false,
      complete: (results) => {
        if (results.errors.length > 0) {
          const errorMsg = results.errors.map(e => e.message).join(', ')
          reject(new Error(`CSV parsing error: ${errorMsg}`))
          return
        }

        let processedData = results.data as any[]

        // Extend Papa's inference for remaining string values such as "null".
        if (inferTypes && !flattenNested) {
          processedData = processedData.map(row => {
            const typedRow: any = {}
            for (const key in row) {
              typedRow[key] = inferType(row[key])
            }
            return typedRow
          })
        }

        // Unflatten if requested (reverse of flatten)
        if (flattenNested === false) {
          // Check if data looks flattened (contains dots in keys)
          const firstRow = processedData[0] || {}
          const hasNestedKeys = Object.keys(firstRow).some(k => k.includes('.'))

          if (hasNestedKeys) {
            processedData = processedData.map(row => unflattenObject(row))
          }
        }

        const fields = results.meta.fields || []

        // Generate preview (first 10 rows)
        const preview: string[][] = [fields]
        const previewRows = processedData.slice(0, 10).map(row =>
          fields.map(f => String(row[f] !== undefined ? row[f] : ''))
        )
        preview.push(...previewRows)

        resolve({
          data: processedData,
          preview,
          fields,
          rowCount: processedData.length
        })
      },
      error: (error: Error) => {
        reject(new Error(`CSV parsing error: ${error.message}`))
      }
    })
  })
}

/**
 * Parse file content (JSON or CSV)
 */
export async function parseFile(file: File): Promise<{
  type: 'json' | 'csv'
  data: any
}> {
  const content = await file.text()
  const fileExt = file.name.toLowerCase().split('.').pop()

  // Try to detect file type
  if (fileExt === 'json') {
    try {
      const parsed = JSON.parse(content)
      return { type: 'json', data: parsed }
    } catch (error) {
      throw new Error('Invalid JSON file: ' + (error as Error).message)
    }
  } else if (fileExt === 'csv' || fileExt === 'txt') {
    return { type: 'csv', data: content }
  } else {
    // Auto-detect
    try {
      const parsed = JSON.parse(content)
      return { type: 'json', data: parsed }
    } catch {
      // Assume CSV
      return { type: 'csv', data: content }
    }
  }
}

/**
 * Download data as file
 */
export function downloadFile(data: string, filename: string, mimeType: string): void {
  const blob = new Blob([data], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Copy to clipboard
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text)
  } else {
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }
}

/**
 * Batch convert files
 */
export async function batchConvert(
  files: File[],
  targetFormat: 'json' | 'csv',
  options: ConversionOptions = {}
): Promise<Array<{ filename: string; data: string; error?: string }>> {
  const results: Array<{ filename: string; data: string; error?: string }> = []

  for (const file of files) {
    try {
      const { type, data: fileData } = await parseFile(file)

      let convertedData: string
      let newExt: string

      if (targetFormat === 'csv' && type === 'json') {
        const result = jsonToCsv(fileData, options)
        convertedData = result.data as string
        newExt = 'csv'
      } else if (targetFormat === 'json' && type === 'csv') {
        const result = await csvToJson(fileData, options)
        convertedData = JSON.stringify(result.data, null, 2)
        newExt = 'json'
      } else {
        throw new Error(`Cannot convert ${type} to ${targetFormat}`)
      }

      const originalName = file.name.replace(/\.[^.]+$/, '')
      results.push({
        filename: `${originalName}.${newExt}`,
        data: convertedData
      })
    } catch (error) {
      results.push({
        filename: file.name,
        data: '',
        error: (error as Error).message
      })
    }
  }

  return results
}
